import type { RowOutcome } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Card, ToneChip, type ToneChipIcon, type ToneChipTone } from '@web/ui/core';

interface OutcomeDisplay {
  tone: ToneChipTone;
  icon: ToneChipIcon;
  label: string;
}

/**
 * Exhaustive map from a `RowOutcome` to its chip tone/icon/label. The `switch`
 * has no `default`: typecheck (`never` in the unreachable branch) enforces that
 * every union variant is handled.
 */
function outcomeDisplay(row: RowOutcome): OutcomeDisplay {
  switch (row.outcome) {
    case 'matched':
      return { tone: 'green', icon: 'check-circle', label: t('scheduleChanges.outcome.matched') };
    case 'voucher-not-found':
      return {
        tone: 'amber',
        icon: 'circle-dashed',
        label: t('scheduleChanges.outcome.voucherNotFound'),
      };
    case 'traveler-unbound':
      return {
        tone: 'amber',
        icon: 'circle-half',
        label: t('scheduleChanges.outcome.travelerUnbound'),
      };
    case 'ambiguous-locator':
      return {
        tone: 'amber',
        icon: 'flag',
        label: t('scheduleChanges.outcome.ambiguousLocator'),
      };
    case 'parse-error':
      return { tone: 'red', icon: 'x-circle', label: t('scheduleChanges.outcome.parseError') };
    case 'enrichment-error':
      return {
        tone: 'red',
        icon: 'x-circle',
        label: t('scheduleChanges.outcome.enrichmentError'),
      };
    default: {
      const _exhaustive: never = row;
      return _exhaustive;
    }
  }
}

const EM_DASH = '—';

/** Localizador cell value — parse-error rows carry no localizador. */
function localizadorOf(row: RowOutcome): string {
  return row.outcome === 'parse-error' ? EM_DASH : row.localizador;
}

/** Stable per-row key from the row's own data — `localizador` (plus the traveler
 *  on a matched row: a group PNR has one per traveler), or the parsed file's
 *  `rowIndex` for parse-error rows (which carry no localizador). */
function rowKey(row: RowOutcome): string {
  switch (row.outcome) {
    case 'parse-error':
      return `parse-error:${String(row.rowIndex)}`;
    case 'matched':
      return `${row.localizador}:${row.userId}`;
    case 'ambiguous-locator':
    case 'enrichment-error':
    case 'traveler-unbound':
    case 'voucher-not-found':
    default:
      return row.localizador;
  }
}

/**
 * Best-effort change summary. Matched rows show a fixed forwarded note;
 * parse/enrichment errors surface their `error` string; the remaining
 * variants carry no extra detail.
 */
function detailsOf(row: RowOutcome): string {
  switch (row.outcome) {
    case 'matched':
      return t('scheduleChanges.details.matched');
    case 'parse-error':
    case 'enrichment-error':
      return row.error;
    case 'ambiguous-locator':
      return t('scheduleChanges.details.ambiguousLocator');
    case 'voucher-not-found':
    case 'traveler-unbound':
      return EM_DASH;
    default: {
      const _exhaustive: never = row;
      return _exhaustive;
    }
  }
}

interface ResultTableProps {
  rows: RowOutcome[];
}

/**
 * Per-row parse outcome table. Renders rows in source order (no sort/group):
 * Localizador · Outcome chip · Details.
 */
export function ResultTable({ rows }: Readonly<ResultTableProps>): React.JSX.Element {
  return (
    <Card>
      <div className="overflow-hidden rounded-lg">
        <table className="w-full border-collapse text-meta">
          <thead>
            <tr>
              <th className="whitespace-nowrap border-b border-border bg-background px-3 py-2 text-left font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                {t('scheduleChanges.table.localizador')}
              </th>
              <th className="whitespace-nowrap border-b border-border bg-background px-3 py-2 text-left font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                {t('scheduleChanges.table.outcome')}
              </th>
              <th className="whitespace-nowrap border-b border-border bg-background px-3 py-2 text-left font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                {t('scheduleChanges.table.details')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const display = outcomeDisplay(row);
              return (
                <tr key={rowKey(row)} className="border-b border-border-soft last:border-b-0">
                  <td className="px-3 py-2 font-mono text-card-id text-foreground">
                    {localizadorOf(row)}
                  </td>
                  <td className="px-3 py-2">
                    <ToneChip tone={display.tone} icon={display.icon}>
                      {display.label}
                    </ToneChip>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{detailsOf(row)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
